import React from 'react';
import PublicBookingWidget from '../components/booking/PublicBookingWidget';

const PublicBooking = () => {
  // Configuration for the booking widget
  const availabilitySettings = {
    startHour: 9,
    endHour: 17,
    slotDuration: 30,
    workingDays: [1, 2, 3, 4, 5], // Monday to Friday
    bufferTime: 15,
    maxAdvanceBooking: 30 // 30 days in advance
  };

  const customization = {
    title: 'Schedule a Meeting',
    description: 'Choose a time that works best for you. We\'ll send you a confirmation email with all the details.',
    primaryColor: '#2563eb',
    companyName: 'CREATILLA'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold text-sm">📅</span>
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                {customization.companyName}
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              Secure booking system
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Let's Schedule a Meeting
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Book a convenient time to discuss your needs. Our team is ready to help you achieve your goals.
            </p>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* Left side - Information */}
            <div className="lg:w-1/2">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  What to Expect
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <span className="text-blue-600 text-sm">⏱️</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        30-Minute Consultation
                      </h4>
                      <p className="text-gray-600 text-sm">
                        We'll discuss your requirements and how we can help you succeed.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <span className="text-green-600 text-sm">🎯</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Personalized Solutions
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Get tailored recommendations based on your specific needs and goals.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <span className="text-purple-600 text-sm">🚀</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Next Steps Planning
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Leave with a clear action plan and timeline for moving forward.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Meeting Options
                  </h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="mr-2">📹</span>
                      <span>Video call (Google Meet)</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2">📞</span>
                      <span>Phone call</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2">🤝</span>
                      <span>In-person meeting</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Testimonial */}
              <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-semibold text-sm">JD</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">John Doe</div>
                    <div className="text-sm text-gray-600">CEO, Tech Startup</div>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "The consultation was incredibly valuable. The team understood our needs immediately and provided actionable insights that helped us grow our business by 40% in just 3 months."
                </p>
                <div className="flex items-center mt-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-sm">⭐</span>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">5.0 rating</span>
                </div>
              </div>
            </div>
            
            {/* Right side - Booking Widget */}
            <div className="lg:w-1/2">
              <PublicBookingWidget 
                availabilitySettings={availabilitySettings}
                customization={customization}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center mr-2">
                <span className="text-white text-xs">🔒</span>
              </div>
              <span className="text-sm text-gray-600">
                Your information is secure and will never be shared
              </span>
            </div>
            <p className="text-xs text-gray-500">
              © 2024 {customization.companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBooking;